import {jsx} from 'panel';

import {QueryBlockEl} from 'common/query-builder/query-block';
import {serializeAttributes} from 'common/util';
import {ButtonTheme} from 'common/components/button/constants';
import {instrumentWithTimer} from 'common/util/workflow-timing';
import {ResourceType} from 'common/report/constants';
import {getIconForEvent} from 'common/report/util/events';
import {PATH_SEPARATOR} from 'common/constants';

import type {QueryEntrySection} from '../index';
import {QuerySuperEntry} from '../types';

export function RowSelector({
  el,
  entry,
  index,
  isPlaceholder,
  selectorIcon,
  handleSelect,
  selected,
}: {
  el: QueryEntrySection;
  entry: QuerySuperEntry;
  index: number;
  isPlaceholder: boolean;
  selectorIcon?: string;
  handleSelect?: (ev: CustomEvent) => void;
  selected?: any;
}) {
  const hasNewUI = el.hasNewQBUI();
  const hasNewCountMenu = el.hasNewCountMenu();
  const {index: activeIndex, part: activePart} = el.state.openEntry;

  const iconMapping = {
    [ResourceType.Cohort]: `community`,
  };

  selectorIcon ||= iconMapping[entry.type] ?? getIconForEvent(entry?.event);

  const hasProperty = `property` in entry && entry.property?.value;

  return (
    <>
      <QueryBlockEl
        key={`entry-selector-${index}${isPlaceholder ? `-placeholder` : ``}`}
        class={{
          'is-disabled': entry.readOnlyEvent,
          'is-placeholder': isPlaceholder,
          'has-property': !hasNewCountMenu && hasProperty,
        }}
        attrs={serializeAttributes({
          ...el.helpers.getQueryBlockConfig(entry, index),
          autofocus: index === 0 && el.attr(`autofocus`) && isPlaceholder && !el.isStagedEntry(0),
          elref: `entrySelector`,
          open: isPlaceholder && activeIndex === index && activePart === el.helpers.EntryPart.Value,
          'data-group-id': el.attr(`data-group-id`),
          'enable-enhanced-list-filters': el.attr(`enable-enhanced-list-filters`),
          'enable-workspace-filter-restrictions': el.attr(`enable-workspace-filter-restrictions`),
          'exclude-custom-events': el.attr(`exclude-custom-events`),
          'hide-edit-controls': el.attr(`hide-edit-controls`),
          'inline-cohorts-override': el.attr(`inline-cohorts-override`),
          'project-has-cohorts': el.attr(`project-has-cohorts`),
          'project-id': el.attr(`project-id`),
          selected: selected ?? (entry.event || null),
          'show-special-dataset-property': el.attr(`show-special-dataset-property`),
          'hide-special-date-property-unit': el.attr(`hide-special-date-property-unit`),
          'show-special-date-property': el.attr(`show-special-date-property`),
          theme: hasNewUI ? ButtonTheme.QueryBuilder : entry.readOnlyEvent ? ButtonTheme.Tertiary : ButtonTheme.Select,
          'track-name': el.attr(`track-name`),
          'selector-icon': hasNewUI ? `` : selectorIcon,
          'selector-type': `button`,
          'user-id': el.attr(`user-id`),
          disabled: el.attr(`disabled`),
          'workspace-id': el.attr(`workspace-id`),
          'prefix-label': entry.prefixLabel,
          'suffix-label': !hasNewCountMenu && hasNewUI && hasProperty && PATH_SEPARATOR,
        })}
        props={{contextPanelParentElement: el.helpers.getContextPanelParentEl(index)}}
        on={{
          close: () => el.helpers.setActiveEntry(`close`, index, el.helpers.EntryPart.Value),
          savedCustomEvent: el.helpers.propagateEvent,
          open: instrumentWithTimer(() => el.helpers.setActiveEntry(`open`, index, el.helpers.EntryPart.Value)),
          remove: () => el.helpers.onEventActionClick(el.helpers.Action.Remove, index),
          select: handleSelect ?? ((ev: CustomEvent) => el.helpers.onEventSelected(ev, index)),
          track: (ev: CustomEvent) => el.helpers.trackEntrySelectorEvent(ev, index),
        }}
      />
    </>
  );
}
